import { Palette } from "app/features/palette/models/palette.model";
import { EtatPalette } from "app/features/palette/enums/etat-palette.enum";
import { Stock } from "app/features/stock/models/stock.model";
import { ChipState } from "../../enums/chip-state.enum";

export type PaletteDisplayStatus =
  | "empty"
  | "preparing"
  | "preparing_on_chargement"
  | "ready"
  | "waiting_shipment"
  | "shipped";

export interface StatusDisplayConfig {
  labelKey: string;
  chipState: ChipState;
  icon: string;
  step: number;
}

export const STATUS_DISPLAY_CONFIG: Record<
  PaletteDisplayStatus,
  StatusDisplayConfig
> = {
  empty: {
    labelKey: "palette_display.status.empty",
    chipState: "enabled",
    icon: "inventory_2",
    step: 0,
  },
  preparing: {
    labelKey: "palette_display.status.preparing",
    chipState: "enabled",
    icon: "pending",
    step: 1,
  },
  preparing_on_chargement: {
    labelKey: "palette_display.status.preparing_on_chargement",
    chipState: "warn",
    icon: "pending",
    step: 1,
  },
  ready: {
    labelKey: "palette_display.status.ready",
    chipState: "accent",
    icon: "check",
    step: 2,
  },
  waiting_shipment: {
    labelKey: "palette_display.status.waiting_shipment",
    chipState: "secondary",
    icon: "link",
    step: 3,
  },
  shipped: {
    labelKey: "palette_display.status.shipped",
    chipState: "primary",
    icon: "check_circle",
    step: 4,
  },
};

export function computePaletteDisplayStatus(
  palette: Palette,
  forTransfertEntrepot: boolean,
): PaletteDisplayStatus {
  if (palette.etat === EtatPalette.EXPEDIEE || palette.dateExpedition) {
    return "shipped";
  }

  const onChargement = forTransfertEntrepot
    ? !!palette.bonTransfertId
    : !!palette.bonLivraisonId;

  if (palette.etat === EtatPalette.PRETE) {
    return onChargement ? "waiting_shipment" : "ready";
  }

  const activeStocks = palette.stocks?.filter((s) => s.quantite !== 0) ?? [];
  if (activeStocks.length > 0) {
    return onChargement ? "preparing_on_chargement" : "preparing";
  }
  return "empty";
}

export type DlcStatus = "ok" | "warning" | "expired";

export function getDlcStatus(
  dluo: Date | string,
  isShipped: boolean,
): DlcStatus {
  if (!dluo || isShipped) return "ok";
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dluoDate = new Date(dluo);
  dluoDate.setHours(0, 0, 0, 0);
  if (dluoDate < now) return "expired";
  const diffDays =
    (dluoDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays <= 7) return "warning";
  return "ok";
}

export interface PaletteAlerts {
  expired: number;
  warning: number;
  missingWeight: number;
}

export function computeAlerts(
  stocks: Stock[],
  isShipped: boolean,
): PaletteAlerts {
  let expired = 0;
  let warning = 0;
  let missingWeight = 0;

  for (const stock of stocks) {
    if (stock.quantite === 0) continue;
    const dlcStatus = getDlcStatus(stock.lotDluo, isShipped);
    if (dlcStatus === "expired") expired++;
    if (dlcStatus === "warning") warning++;
    if (!stock.articleInfos?.gross_weight_uc) missingWeight++;
  }

  return { expired, warning, missingWeight };
}

export interface PaletteWeights {
  produits: number;
  tare: number;
  brut: number;
}

export function computeWeights(
  stocks: Stock[],
  customTare?: number,
  defaultTare?: number,
  coefficients?: Map<string, number>,
): PaletteWeights {
  const activeStocks = stocks.filter((s) => s.quantite !== 0);
  const poidsProduits = activeStocks.reduce(
    (sum, s) => {
      const coeff = coefficients?.get(s.articleId) ?? 1;
      return sum + s.quantite * (s.articleInfos?.gross_weight_uc ?? 0);
    },
    0,
  );
  const tare = customTare ?? defaultTare ?? 0;
  return { produits: poidsProduits, tare, brut: poidsProduits + tare };
}
